import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Pencil, Plus, QrCode, Trash2 } from "lucide-react";
import { WasherForm } from "./WasherForm";
import { QrDialog } from "../QrDialog";
import { Stars } from "../Stars";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import axios, { API, authed, errMsg, photoSrc } from "../../lib/api";

const IconBtn = ({ children, ...p }) => (
  <button {...p} className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-slate-300 transition-colors hover:border-[#00F0FF]/60 hover:text-white">{children}</button>
);

export const WashersAdmin = ({ onChange }) => {
  const { getToken } = useAuth();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [qr, setQr] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/admin/washers`, await authed(getToken));
    setList(r.data);
  }, [getToken]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChange(); };

  const remove = async () => {
    try {
      await axios.delete(`${API}/admin/washers/${toDelete.id}`, await authed(getToken));
      toast.success("Spălătorul a fost șters.");
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };

  return (
    <div>
      <button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-washer-btn"
        className="mb-5 flex items-center gap-2 rounded-full bg-[#00F0FF] px-5 py-2.5 text-sm font-semibold text-[#090E17] hover:bg-[#33F3FF]">
        <Plus size={17} /> Adaugă spălător
      </button>
      <ul className="space-y-3" data-testid="admin-washers-list">
        {list.map((w) => (
          <li key={w.id} data-testid={`admin-washer-row-${w.id}`} className="flex items-center gap-4 rounded-2xl border border-white/[0.07] bg-[#131B26] p-4">
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-slate-800">
              {w.photo_url && <img src={photoSrc(w.photo_url)} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="truncate font-semibold">{w.name}</p>
                <span data-testid={`admin-washer-status-${w.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${w.active ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>
                  {w.active ? "Activ" : "Inactiv"}
                </span>
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                <Stars value={w.avg_rating} size={12} /> {w.review_count ? w.avg_rating.toFixed(1) : "–"} · {w.review_count} {w.review_count === 1 ? "recenzie" : "recenzii"}
              </div>
            </div>
            <div className="flex gap-2">
              <IconBtn onClick={() => setQr(w)} data-testid={`admin-washer-qr-${w.id}`} aria-label="Cod QR"><QrCode size={15} /></IconBtn>
              <IconBtn onClick={() => { setEditing(w); setFormOpen(true); }} data-testid={`admin-washer-edit-${w.id}`} aria-label="Editează"><Pencil size={15} /></IconBtn>
              <IconBtn onClick={() => setToDelete(w)} data-testid={`admin-washer-delete-${w.id}`} aria-label="Șterge"><Trash2 size={15} /></IconBtn>
            </div>
          </li>
        ))}
      </ul>
      <WasherForm washer={editing} open={formOpen} onOpenChange={setFormOpen} onSaved={refresh} />
      <QrDialog washer={qr} open={!!qr} onOpenChange={(o) => !o && setQr(null)} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-[#131B26]">
          <AlertDialogHeader>
            <AlertDialogTitle>Ștergi pe {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Se șterg și toate recenziile lui. Dacă vrei doar să-l ascunzi, dezactivează-l din editare.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Renunță</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="delete-confirm-btn" className="bg-[#F43F5E] text-white hover:bg-[#F43F5E]/90">Șterge</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
